import { useTranslation } from 'react-i18next';
import type { SubscriptionCatalog, SubscriptionSnapshot } from '@tabbio/types';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  formatCancelAccessDate,
  getPlanBenefits,
  getValueAllowances,
} from '../../lib/cancel-retention';

export function SubscriptionValue({
  catalog,
  snapshot,
  planLabel,
  showAllowances = false,
}: {
  catalog: SubscriptionCatalog;
  snapshot: SubscriptionSnapshot | null;
  planLabel: string;
  showAllowances?: boolean;
}) {
  const { t, i18n } = useTranslation('common');
  const allowances = getValueAllowances(snapshot);
  const activity = allowances.filter((item) => item.used > 0).slice(0, 3);
  const benefits = getPlanBenefits(catalog, snapshot);
  const number = (value: number) =>
    new Intl.NumberFormat(i18n.language).format(value);
  const heading = {
    fontSize: 16,
    fontWeight: 500,
    margin: '0 0 14px',
    color: 'var(--text-primary)',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {activity.length > 0 ? (
        <section>
          <h3 style={heading}>{t('settings.subscriptionValue.activity')}</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: 8,
            }}
          >
            {activity.map((item) => (
              <div
                key={item.actionKey}
                style={{
                  padding: 14,
                  background: 'var(--hover)',
                  borderRadius: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 400,
                    lineHeight: 1.1,
                    color: 'var(--text-primary)',
                  }}
                >
                  {number(item.used)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  {t(`settings.subscriptionValue.actions.${item.label}`)}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {t(`settings.subscriptionValue.period.${item.cadence}`)}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {benefits.length > 0 ? (
        <section>
          <h3 style={heading}>
            {t('settings.subscriptionValue.benefits', { plan: planLabel })}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {benefits.map((key) => (
              <div
                key={key}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <span
                  aria-hidden="true"
                  style={{ color: 'var(--text-secondary)', paddingTop: 2 }}
                >
                  <MaterialSymbol
                    name={
                      key === 'automation'
                        ? 'schedule'
                        : key === 'interview' || key === 'outreach'
                          ? 'chat_bubble'
                          : key === 'cv' || key === 'letters'
                            ? 'description'
                            : 'work_outline'
                    }
                    size={22}
                  />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t(`settings.subscriptionValue.benefit.${key}.title`)}
                  </div>
                  <p
                    style={{
                      margin: '3px 0 0',
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {t(`settings.subscriptionValue.benefit.${key}.body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {showAllowances && allowances.length > 0 ? (
        <section>
          <h3 style={heading}>{t('settings.subscriptionValue.allowances')}</h3>
          {allowances.map((item) => (
            <div
              key={item.actionKey}
              style={{
                paddingBlock: 10,
                borderTop: '1px solid var(--input-border)',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>
                  {t(`settings.subscriptionValue.actions.${item.label}`)}
                </span>
                <span>
                  {t('settings.subscriptionValue.remaining', {
                    remaining: number(item.remaining),
                    limit: number(item.limit),
                  })}
                </span>
              </div>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                }}
              >
                {t('settings.subscriptionValue.resets', {
                  date: formatCancelAccessDate(item.resetAt, i18n.language),
                })}
              </p>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
